import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getRenderBufferHeight } from '../scene-manager.js';
import { vdjLoop, vdjBeatBreath, vdjBarChroma, VdjPost, VdjComfort, VDJ_PRESETS, VDJ_MIRROR_CHUNK, VDJ_MIRROR_KALEIDO8 } from './lib/vdjLook.js';
import { genererMatcap, MATCAP_GAIN } from './totem.js';

// Bloom - three to five giant chrome and iridescent flowers opening and
// closing slowly around the cover, on black, in a faint kaleidoscope.
// Downtempo, trip-hop, soul, electronic jazz, soft pop. Applies the LOOK VDJ
// LOUNGE preset (VDJ_PRESETS.lounge in lib/vdjLook.js): cuts one phrase in
// two only, with a one-bar fade; loops of 16, 32 and 64 beats; the beat a
// breath (+8%) on the stamens alone; RGB split on the bar (x1.5, 300 ms);
// a slow drift of the whole bouquet instead of a camera kick; a drop
// without a flash (+25% over a bar, every flower open for 8 bars); comfort
// capped at 0.35 with no full-screen flash at all.
//
// THE PETAL is a parametric surface, s along it (0 at the base, 1 at the
// tip) and v across (-1..1): a teardrop, bent along its length and cupped
// like a spoon. Its whole shape is a function of ONE number, the crown's
// opening o (0 = closed bud, 1 = open): the base angle off the flower's
// axis and the bend along the petal both move with it, so opening a flower
// is a single uniform per flower. The SAME function is exported below
// (petalLocal) for the build-free intersection test: two rules make petals
// never pass through each other, and the test checks both at every opening:
//   - a petal stays inside its own angular wedge (its half-width is
//     clamped to WEDGE x tan(pi / n) of its distance to the axis, with the
//     cup's inward pull accounted for), so a crown cannot self-intersect;
//   - an inner crown is never more open than the crown around it
//     (crownOpen: the outer crown leads, the inner ones lag by DELAY), so
//     the crowns stay nested. Opening propagates from the outside in, and
//     closing from the inside out - the bud wraps itself up last.
//
// ALL THE PETALS OF ALL THE FLOWERS are ONE InstancedMesh: static instance
// attributes (flower, crown, index in the crown), and per-flower data in
// small uniform arrays the vertex shader indexes (allowed in a WebGL1
// vertex shader): the flower's matrix, its opening, its level and its hue.
//
// THE LIGHT is Totem's matcap (genererMatcap, built on lib/matcap.js's
// studio since the 13/10 corrections: ~40% of the ball in near-black, two
// wide light boxes, saturated iridescent bands following LEVEL CURVES).
// IRIDESCENT on the inner face; on the back, CHROME tinted 30% by the
// flower's colour and darkened to 0.5 - a dark coloured chrome, never a
// brown flat. A matcap is lit by the VIEW-SPACE normal, so the normals are
// the whole look: they are derived in the vertex shader from the surface
// ITSELF (finite differences of petalLocal at 1/64, opening included) and
// interpolated smoothly - a wrong or flat normal reads a single texel of
// the matcap over a whole petal, which is what flat colour patches are.
// With a deep spoon (cup) and 16 vertices across, a petal's normals sweep
// across the matcap and the light boxes glide along it as it opens.
//
// ANTI-ALIASING: the shared pipeline has none. Bloom renders its flowers
// into a private 4x MSAA target shown by a full-frame quad (metronome.js's
// recipe, see the no-antialiasing memory) - ?bloomaa=msaa, the default.
// ?bloomaa=edge is the fallback: the petal's edge is known by its
// parameters (|v| = 1 the sides, s = 1 the tip), so a 1.5-pixel alpha ramp
// from fwidth() softens it, the petals then blended in order (flowers back
// to front, crowns outside in). ?bloomaa=off for measuring.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'bloom',
  // The brief says 'objects'; the orchestrator's family is 'object' (Totem
  // and eleven others) - an unknown family would only isolate this one.
  family: 'object',
  danceability: 0.52,
  energyLevel: 0.40,
  bpmMin: 70,
  bpmMax: 115,
  movement: 0.40,
  tonality: 0.85,
  enabled: true
};

const LOOK = VDJ_PRESETS.lounge;

// --- the petal ---------------------------------------------------------------
// minHalf: a floor on the half-width, x the length - the inner crown's
// petals were needles (the wedge clamp, ten petals round a small base) and
// read as WHITE SPIKES at the heart; they are now at least 35% of their
// length wide, which no longer fits side by side in a bud, so they overlap
// like aperture blades: `shingle` turns each about its long axis (its +v
// edge in, its -v edge out), every one tucked under the next.
// aMin: the inner crown never leans less than 15 degrees off the flower's
// axis (which faces the camera), so it is never seen edge-on.
// The buds are looser than the first version's (closed angles 0.62 / 0.56 /
// 0.50) so that the outer crowns still wrap the wider inner one.
export const CROWNS = [
  { n: 6, L: 1.00, rb: 0.14, zb: 0.00, W: 0.46, aC: 0.62, aO: 1.40, kC: -0.30, kO: 0.30, minHalf: 0, shingle: 0, aMin: 0 },
  { n: 8, L: 0.80, rb: 0.10, zb: 0.05, W: 0.34, aC: 0.56, aO: 1.12, kC: -0.27, kO: 0.24, minHalf: 0, shingle: 0, aMin: 0 },
  { n: 10, L: 0.60, rb: 0.06, zb: 0.10, W: 0.24, aC: 0.50, aO: 0.86, kC: -0.24, kO: 0.18, minHalf: 0.175, shingle: 0.30, aMin: 15 * Math.PI / 180 }
];
export const CUP = [0.55, 0.36];      // the spoon's depth, x half-width: closed, open (a deep spoon is what the reflections read)
export const DELAY = 0.30;            // each crown lags the one around it by this much opening
export const WEDGE = 0.95;            // fraction of its wedge a petal may fill (0.96 measured clean)
const SEG_S = 31, SEG_V = 15;         // 32 x 16 vertices
const SHAPE_NORM = 0.451;             // max of s^0.55 (1-s)^0.6
const NORMAL_EPS = 1 / 64;            // the finite-difference step of the normals

export function crownOpen(O, c, crowns) {
  const k = 1 + DELAY * ((crowns || CROWNS.length) - 1);
  return Math.max(0, Math.min(1, O * k - c * DELAY));
}

// (s, v) on crown c at opening o -> point in the flower's frame (axis +z),
// before the petal's turn about the axis. The vertex shader's petalLocal()
// is this function, line for line.
const WIDTH_SMOOTH = 0.03;            // the smooth min/max's blend, in width units
function smin(a, b, k) { const h = Math.max(k - Math.abs(a - b), 0) / k; return Math.min(a, b) - h * h * k * 0.25; }
function smax(a, b, k) { const h = Math.max(k - Math.abs(a - b), 0) / k; return Math.max(a, b) + h * h * k * 0.25; }

export function petalLocal(s, v, o, c, out) {
  const C = CROWNS[c];
  let a0 = C.aC + (C.aO - C.aC) * o;
  const k = C.kC + (C.kO - C.kC) * o;
  // the inclination floor holds along the whole petal: a(s) = a0 + k s >= aMin
  a0 = Math.max(a0, C.aMin - Math.min(k, 0));
  const a = a0 + k * s;
  let sx, sz;
  if (Math.abs(k) < 1e-4) { sx = s * Math.sin(a0); sz = s * Math.cos(a0); }
  else { sx = (Math.cos(a0) - Math.cos(a)) / k; sz = (Math.sin(a) - Math.sin(a0)) / k; }
  const r = C.rb + C.L * sx, z = C.zb + C.L * sz;
  const cup = CUP[0] + (CUP[1] - CUP[0]) * o;
  const kt = Math.tan(Math.PI / C.n) * WEDGE;
  const ca = Math.cos(a), sa = Math.sin(a);
  const sh = Math.pow(s, 0.55) * Math.pow(Math.max(0, 1 - s), 0.6) / SHAPE_NORM;
  let w = C.W * C.L * sh;
  const lim = kt * r / Math.max(1e-3, 1 + kt * cup * ca);
  // SMOOTH min / max: a hard min put a kink in the width where it meets the
  // wedge's ceiling, and the 1/64 differences straddling it gave normals 17
  // degrees off (0.2% of the vertices). smin stays BELOW the true min, so the
  // wedge's no-intersection guarantee holds; smax stays above the floor.
  w = smin(w, smax(lim, C.minHalf * C.L * sh, WIDTH_SMOOTH), WIDTH_SMOOTH);
  if (w < 0) w = 0;
  // along the inner normal N = (-cos a, 0, sin a): the spoon (v^2) and the shingle (v)
  const off = cup * w * v * v + C.shingle * w * v;
  out[0] = r - ca * off;
  out[1] = v * w;
  out[2] = z + sa * off;
  return out;
}

// The open flower's bounding radius at scale 1, from the surface itself:
// what the composition uses to keep a flower in the frame and off the cover.
export const R_BOUND = (function () {
  let R = 0;
  const p = [0, 0, 0];
  for (let k = 0; k <= 10; k++) for (let c = 0; c < CROWNS.length; c++) for (let j = 0; j <= 32; j++) for (let i = -4; i <= 4; i++) {
    petalLocal(j / 32, i / 4, crownOpen(k / 10, c), c, p);
    R = Math.max(R, Math.hypot(p[0], p[1], p[2]));
  }
  return R * 1.02;
})();

// --- the flowers and the composition -----------------------------------------
const MAX_FLOWERS = 5;
const TIER = {
  2: { flowers: 5, crowns: 3, bgScale: 0.5 },
  // no rosace at tier 1: it cost 8 ms on the Pi (43 -> 35 with MSAA) for a
  // motif under 2/255 that the cover mostly hides
  1: { flowers: 4, crowns: 3, bgScale: 0 },
  0: { flowers: 3, crowns: 2, bgScale: 0 }
};
const CAM_Z = 10, CAM_FOV = 46;
// The slots, in the safe zone's height units (x scaled by the aspect):
// where a flower may stand, how far, how big it wants to be on screen. The
// size actually given is the largest that keeps the open flower off the
// cover (and its margin); a slot too close to the cover is skipped.
// A slot's centre is only a wish: the solver (_solveSlot) pulls it in until
// the whole open flower sits inside the frame with EDGE_MARGIN, off the
// cover, and out of the Broadcast button's corner.
const SLOTS = [
  { x: -0.68, y: -0.42, d: 8.0, r: 0.45 },   // the big one, bottom left
  { x: 0.64, y: 0.30, d: 15.0, r: 0.32 },    // at the back, upper right (under the Broadcast corner)
  { x: -0.70, y: 0.55, d: 11.0, r: 0.28 },
  { x: 0.70, y: -0.52, d: 10.0, r: 0.30 },
  { x: -0.92, y: 0.05, d: 12.5, r: 0.26 },
  { x: 0.92, y: -0.10, d: 12.5, r: 0.26 },
  { x: 0.40, y: -0.80, d: 11.5, r: 0.26 },
  { x: -0.38, y: 0.82, d: 13.5, r: 0.26 },
  // smaller places to move to, so a flower coming back always finds one
  { x: -0.40, y: -0.85, d: 12.0, r: 0.22 },
  { x: 0.10, y: -0.88, d: 13.0, r: 0.20 },
  { x: -0.95, y: -0.62, d: 12.0, r: 0.22 },
  { x: 0.96, y: 0.40, d: 13.0, r: 0.22 }
];
const MAX_R = 0.45;                 // the biggest flower: 45% of the screen's height across (0.45 height units radius)
const EDGE_MARGIN = 0.10;           // 5% of the screen's height, in height units (the height is 2)
// The Broadcast button's corner: 22% of the width x 12% of the height, top right.
const BROADCAST_W = 0.22, BROADCAST_H = 0.12, BROADCAST_MARGIN = 0.03;
const COVER_MARGIN = 0.07;          // height units between an open flower and the cover
const DRIFT_MARGIN = 0.05;          // the bouquet's drift (zoom 2%, roll 1.2 deg) eats this much
const MIN_SLOT_R = 0.12;             // height units: under this a slot is not taken (at 16:9 the fifth flower gets 0.13)
const FLOWER_GAP = 0.05;            // height units between two flowers' discs on screen
const SLOT_ORDER = [0, 1, 2, 3, 5, 4, 6, 7, 8, 9, 10, 11];   // the initial deal (every slot, the big ones first)
const ANCHORS = [0, 1];
const TILT = 0.42;                  // a flower's axis leans this much away from the camera, outward
const SPIN_BEATS = 64;              // one turn of a flower about its axis

// --- the cycle ------------------------------------------------------------------
// open 8 bars, hold 8 (breathing 0.85..1 on the bar), close 8, away the rest
// (recede, move, come back as a bud). The cycle is 8 bars per flower but at
// least 32, and the flowers are spread evenly over it: with four or five
// there is always exactly one opening and one closing.
const OPEN_BARS = 8, HOLD_BARS = 8, CLOSE_BARS = 8;
const BREATH_DEPTH = 0.15;          // the hold's breathing: 1 - 0.15 at its deepest
const RECEDE = 0.7;                 // an away flower goes 70% further
const DROP_OPEN_BARS = 2, DROP_HOLD_BARS = 8, DROP_RETURN_BARS = 2;
const KNEE_FROM = 0.50, KNEE_TO = 0.74;   // the petals' luminance roll-off, under the bloom threshold (0.78)
const HUE_GRAD = 0.06;              // turns of hue along a petal: the colours flow tip-ward
// Totem's studio, two Bloom settings (see genererMatcap): the light boxes'
// edge softened over 10 matcap pixels - on a petal a hard edge followed the
// triangles in a broken line - and the iridescent bands sweeping a third of
// the hue circle instead of a whole rainbow, so each flower keeps its own
// colour with a nacre shift around it rather than being a colour wheel.
const MATCAP_OPTS = { edgePx: 10, hueSpan: 0.35 };
const BACK_TINT = 0.30, BACK_GAIN = 0.50;   // the back: chrome x the flower's colour at 30%, at half

// --- the background --------------------------------------------------------------
// "15%" is meant on screen: applied in linear light it read as 42% (a grey
// wall of rosace, frame mean 0.44). 0.15 displayed is 0.15^2.2 = 0.016 linear.
const BG_DARK = Math.pow(0.15, 2.2), BG_ZOOM = 2.5, BG_SAFE_FLOOR = 0.5;
// The rosace is a motif with black around it, not a wall: the flowers of
// the background pass sit close to the target's centre and small enough
// that, magnified BG_ZOOM times, they fill about the inner two thirds of
// the frame's height. At first they filled all of it (frame median 41/255,
// no black pixel left).
const BG_R = 0.42, BG_FLOWER_R = 0.50, BG_TURN_BEATS = 64;

// --- the stamens --------------------------------------------------------------------
const STAMENS = 40;
const STAMEN_PX = 7.0;

const LUM_EVERY = 30;
const EXPOSURE = 1.0;
const DROP_SCORE_MIN = 0.7;
const FALLBACK_COLORS = [0xff5aa0, 0x5ac8ff, 0xffb347, 0x9b7bff, 0x4fe0b0];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}

// =============================================================================
// SHADERS
// =============================================================================

function crownGlsl(c) {
  const C = CROWNS[c];
  return '{ a = vec4(' + C.n + '.0, ' + C.L.toFixed(3) + ', ' + C.rb.toFixed(3) + ', ' + C.zb.toFixed(3) + '); b = vec4(' + C.W.toFixed(3) + ', ' + C.aC.toFixed(3) + ', ' + C.aO.toFixed(3) + ', ' + C.kC.toFixed(3) + '); e = vec4(' + C.kO.toFixed(3) + ', ' + C.minHalf.toFixed(3) + ', ' + C.shingle.toFixed(3) + ', ' + C.aMin.toFixed(4) + '); }';
}

const CROWN_GLSL = `
  // CROWNS[c] - a (n, L, rb, zb), b (W, aC, aO, kC), e (kO, minHalf, shingle, aMin)
  void crownParams(float c, out vec4 a, out vec4 b, out vec4 e) {
    if (c < 0.5) ${crownGlsl(0)}
    else if (c < 1.5) ${crownGlsl(1)}
    else ${crownGlsl(2)}
  }

  float smin(float a, float b, float k) { float h = max(k - abs(a - b), 0.0) / k; return min(a, b) - h * h * k * 0.25; }
  float smax(float a, float b, float k) { float h = max(k - abs(a - b), 0.0) / k; return max(a, b) + h * h * k * 0.25; }

  // petalLocal() of bloom.js, line for line.
  vec3 petalLocal(float s, float v, float o, float c) {
    vec4 A, B, E;
    crownParams(c, A, B, E);
    float n = A.x, L = A.y, rb = A.z, zb = A.w;
    float a0 = B.y + (B.z - B.y) * o;
    float k = B.w + (E.x - B.w) * o;
    a0 = max(a0, E.w - min(k, 0.0));
    float a = a0 + k * s;
    float sx, sz;
    if (abs(k) < 1e-4) { sx = s * sin(a0); sz = s * cos(a0); }
    else { sx = (cos(a0) - cos(a)) / k; sz = (sin(a) - sin(a0)) / k; }
    float r = rb + L * sx, z = zb + L * sz;
    float cup = ${CUP[0].toFixed(3)} + (${CUP[1].toFixed(3)} - ${CUP[0].toFixed(3)}) * o;
    float kt = tan(3.14159265 / n) * ${WEDGE.toFixed(3)};
    float ca = cos(a), sa = sin(a);
    float sh = pow(max(s, 0.0), 0.55) * pow(max(1.0 - s, 0.0), 0.6) / ${SHAPE_NORM.toFixed(3)};
    float w = B.x * L * sh;
    float lim = kt * r / max(1e-3, 1.0 + kt * cup * ca);
    w = max(smin(w, smax(lim, E.y * L * sh, ${WIDTH_SMOOTH.toFixed(3)}), ${WIDTH_SMOOTH.toFixed(3)}), 0.0);
    float off = cup * w * v * v + E.z * w * v;
    return vec3(r - ca * off, v * w, z + sa * off);
  }

  // the inner side's normal at s - only a fallback where the surface is
  // degenerate (the base, where the width is zero)
  vec3 innerNormal(float s, float o, float c) {
    vec4 A, B, E;
    crownParams(c, A, B, E);
    float a0 = B.y + (B.z - B.y) * o;
    float k = B.w + (E.x - B.w) * o;
    a0 = max(a0, E.w - min(k, 0.0));
    float a = a0 + k * s;
    return vec3(-cos(a), 0.0, sin(a));
  }

  float crownOpen(float O, float c) {
    float k = 1.0 + ${DELAY.toFixed(3)} * (uCrowns - 1.0);
    return clamp(O * k - c * ${DELAY.toFixed(3)}, 0.0, 1.0);
  }
`;

const PETAL_VERTEX = `
  precision highp float;
  attribute float aFlower;
  attribute float aCrown;
  attribute float aIndex;
  uniform mat4 uFlower[${MAX_FLOWERS}];
  uniform float uOpen[${MAX_FLOWERS}];
  uniform float uLevel[${MAX_FLOWERS}];
  uniform float uHue[${MAX_FLOWERS}];
  uniform vec3 uColor[${MAX_FLOWERS}];
  uniform float uCrowns;
  varying vec3 vN;
  varying vec3 vView;
  varying float vLevel;
  varying float vHue;
  varying float vS;
  varying float vV;
  varying vec3 vTint;

  ${CROWN_GLSL}

  void main() {
    int f = int(aFlower + 0.5);
    float o = crownOpen(uOpen[f], aCrown);
    float s = position.x, v = position.y;
    vec3 p = petalLocal(s, v, o, aCrown);
    // THE NORMAL, from the deformed surface itself: the same petalLocal
    // (opening, bend, cup, shingle included) one step of 1/64 further along
    // s and along v - backwards at the far edges - then cross(dP/ds, dP/dv),
    // which points to the inner face for a positive step (and is the
    // winding the index buffer gives the triangles, so gl_FrontFacing
    // agrees with it). Smooth: one normal per vertex, interpolated.
    float e = ${NORMAL_EPS.toFixed(6)};
    float fs = s + e <= 1.0 ? 1.0 : -1.0;
    float fv = v + e <= 1.0 ? 1.0 : -1.0;
    vec3 ds = (petalLocal(s + fs * e, v, o, aCrown) - p) * fs;
    vec3 dv = (petalLocal(s, v + fv * e, o, aCrown) - p) * fv;
    vec3 n = cross(ds, dv);
    float nl = length(n);
    n = nl > 1e-9 ? n / nl : innerNormal(s, o, aCrown);
    // the petal's turn about the flower's axis
    vec4 A, B, E;
    crownParams(aCrown, A, B, E);
    float phi = 6.2831853 * (aIndex + 0.5 * aCrown) / A.x;
    float cp = cos(phi), sp = sin(phi);
    p.xy = vec2(cp * p.x - sp * p.y, sp * p.x + cp * p.y);
    n.xy = vec2(cp * n.x - sp * n.y, sp * n.x + cp * n.y);
    // the flower's matrix comes in a uniform (one draw for every flower), so
    // the mesh's own normalMatrix is not this petal's: its equivalent is
    // mat3(view x flower), exact here because every flower is scaled
    // uniformly (a rotation times a scalar, renormalised)
    mat4 M = uFlower[f];
    vec4 mv = viewMatrix * (M * vec4(p, 1.0));
    vView = mv.xyz;
    vN = normalize(mat3(viewMatrix) * (mat3(M) * n));
    vLevel = uLevel[f];
    vHue = uHue[f];
    vTint = uColor[f];
    vS = s;
    vV = v;
    gl_Position = projectionMatrix * mv;
  }
`;

const PETAL_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uIri;
  uniform sampler2D uChrome;
  uniform float uExposure;
  uniform float uEdgeAA;        // ?bloomaa=edge: the parametric edge ramp
  uniform float uShowNormals;   // ?bloomnormals=1
  varying vec3 vN;
  varying vec3 vView;
  varying float vLevel;
  varying float vHue;
  varying float vS;
  varying float vV;
  varying vec3 vTint;

  vec3 hueShift(vec3 c, float h) {
    const vec3 k = vec3(0.57735027);
    float cs = cos(h), sn = sin(h);
    return max(c * cs + cross(k, c) * sn + k * dot(k, c) * (1.0 - cs), 0.0);
  }

  void main() {
    // the inner face is the triangles' front face (see the vertex shader);
    // the back's normal is the same normal, inverted
    vec3 n = normalize(vN);
    if (!gl_FrontFacing) n = -n;
    if (uShowNormals > 0.5) { gl_FragColor = vec4(n * 0.5 + 0.5, 1.0); return; }
    vec3 V = normalize(-vView);
    vec3 nm = n.z < 0.0 ? normalize(vec3(n.xy, 0.0) + vec3(0.0, 0.0, 0.02)) : n;
    vec2 muv = nm.xy * 0.49 + 0.5;
    vec3 c;
    if (gl_FrontFacing) {
      c = texture2D(uIri, muv).rgb;
      c = c * c * ${MATCAP_GAIN.toFixed(2)};
      c = hueShift(c, (vHue + ${HUE_GRAD.toFixed(3)} * vS) * 6.2831853);
    } else {
      // the back: chrome, tinted 30% by the flower's colour, at half - a
      // dark coloured chrome with its reflections, never a brown flat
      c = texture2D(uChrome, muv).rgb;
      c = c * c * ${MATCAP_GAIN.toFixed(2)};
      vec3 tint = vTint / max(max(vTint.r, max(vTint.g, vTint.b)), 1e-3);
      c *= mix(vec3(1.0), tint, ${BACK_TINT.toFixed(2)}) * ${BACK_GAIN.toFixed(2)};
    }
    c *= vLevel * uExposure;
    // a petal turning edge-on to the camera goes dark, never to a white
    // sliver: the matcap's rim is its brightest ring, exactly where a
    // grazing normal reads it
    c *= smoothstep(0.04, 0.30, abs(dot(n, V)));
    // THE KNEE. UnrealBloom's high pass lets through the WHOLE colour of
    // every pixel above the threshold, so what feeds the halo is the AREA
    // over 0.78, not its excess - and a giant flower facing the camera puts
    // the matcap's light boxes over 12% of the frame (Totem's sculptures:
    // under 1%). Its widest blur then lifted the whole frame (median 49/255,
    // not one black pixel left). Luminance above KNEE_FROM is rolled off
    // towards KNEE_TO, under the threshold: the boxes stay the brightest
    // thing on a petal, and the white cores above it are the stamens'.
    float L = dot(c, vec3(0.2126, 0.7152, 0.0722));
    if (L > ${KNEE_FROM.toFixed(3)}) {
      float span = ${(KNEE_TO - KNEE_FROM).toFixed(3)};
      float Lk = ${KNEE_FROM.toFixed(3)} + span * (1.0 - exp(-(L - ${KNEE_FROM.toFixed(3)}) / span));
      c *= Lk / L;
    }
    // the parametric edge: |v| = 1 the sides, s = 1 the tip; fwidth gives
    // the parameter's change per pixel, so this is a 1.5-pixel ramp
    float alpha = 1.0;
    if (uEdgeAA > 0.5) {
      float side = (1.0 - abs(vV)) / max(fwidth(vV), 1e-5);
      float tip = (1.0 - vS) / max(fwidth(vS), 1e-5);
      alpha = smoothstep(0.0, 1.5, min(side, tip));
    }
    // premultiplied: the blending is (ONE, ONE_MINUS_SRC_ALPHA), opaque at 1
    gl_FragColor = vec4(c * alpha, alpha);
  }
`;

// ?bloommatcap=1: the two matcaps side by side, to check them by eye.
const MATCAP_VIEW_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uA; uniform sampler2D uB;
  uniform float uAspect;
  varying vec2 vUv;
  void main() {
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0) * 1.1;
    vec3 c = vec3(0.0);
    for (int k = 0; k < 2; k++) {
      vec2 q = (p - vec2(float(k) * 0.9 - 0.45, 0.0)) / 0.42;
      if (dot(q, q) < 1.0) {
        vec2 uv = q * 0.5 + 0.5;
        c = k == 0 ? texture2D(uA, uv).rgb : texture2D(uB, uv).rgb;
        c = c * c * ${MATCAP_GAIN.toFixed(2)};
      }
    }
    gl_FragColor = vec4(c, 1.0);
  }
`;

// The private MSAA target's image, laid over the rosace: premultiplied.
const DISPLAY_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = texture2D(uTex, vUv); }
`;

// The stamens: a small sphere of points at the heart of each flower,
// growing out as it opens. White cores (above the bloom threshold), halos
// in the flower's colour, a breath on the beat, a twinkle on bands.high.
const STAMEN_VERTEX = `
  precision highp float;
  attribute float aFlower;
  attribute vec3 aDir;
  attribute float aSeed;
  uniform mat4 uFlower[${MAX_FLOWERS}];
  uniform float uOpen[${MAX_FLOWERS}];
  uniform float uLevel[${MAX_FLOWERS}];
  uniform vec3 uColor[${MAX_FLOWERS}];
  uniform float uPx;
  uniform float uTime;
  uniform float uTwinkle;
  uniform float uBreath;
  varying vec3 vColor;
  varying float vBright;
  void main() {
    int f = int(aFlower + 0.5);
    float O = uOpen[f];
    float r = 0.07 + 0.20 * O;
    vec3 p = vec3(0.0, 0.0, 0.10 + 0.10 * O) + aDir * r * vec3(1.0, 1.0, 0.75);
    vec4 world = uFlower[f] * vec4(p, 1.0);
    vec4 mv = viewMatrix * world;
    float scale = length(uFlower[f][0].xyz);
    gl_PointSize = uPx * scale * (0.6 + 0.8 * aSeed) / max(-mv.z, 0.1);
    gl_Position = projectionMatrix * mv;
    float tw = 1.0 + uTwinkle * sin(aSeed * 61.0 + mod(uTime, 62.83) * (2.0 + 4.0 * aSeed));
    vColor = uColor[f];
    vBright = uLevel[f] * smoothstep(0.05, 0.45, O) * uBreath * tw;
  }
`;
const STAMEN_FRAGMENT = `
  precision mediump float;
  uniform float uExposure;
  varying vec3 vColor;
  varying float vBright;
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float r = length(d) * 2.0;
    if (r > 1.0) discard;
    float core = 1.0 - smoothstep(0.0, 0.35, r);
    float halo = (1.0 - r) * (1.0 - r);
    // additive: forty of them overlap, so each core only just clears the
    // bloom threshold where it stands alone
    vec3 c = vColor * halo * 0.30 + vec3(0.85) * core;
    gl_FragColor = vec4(c * vBright * uExposure, 1.0);
  }
`;

const QUAD_VERTEX = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// The far rosace: the flowers rendered again at half resolution, folded
// into eight wedges, magnified and darkened to 15%. The safe-zone floor
// (0.5) applies here only - the flowers in front avoid the cover by
// construction.
const BG_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  uniform float uAspect;
  uniform float uZoom;
  uniform float uDark;
  uniform float uRot;
  uniform float uShift;
  uniform vec2 uTexel;
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  ${VDJ_MIRROR_CHUNK}
  float safeAtt(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${BG_SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  void main() {
    vec2 d = (vUv - vec2(0.5, 0.5 + uShift)) * vec2(uAspect, 1.0);
    float cr = cos(uRot), sr = sin(uRot);
    d = vec2(cr * d.x - sr * d.y, sr * d.x + cr * d.y);
    vec2 uv = vdjMirror(d / vec2(uAspect, 1.0) + 0.5, ${VDJ_MIRROR_KALEIDO8}.0, uAspect);
    uv = (uv - 0.5) / uZoom + 0.5;
    vec3 c = texture2D(uTex, uv + uTexel * vec2(0.5, 0.5)).rgb + texture2D(uTex, uv + uTexel * vec2(-0.5, 0.5)).rgb
           + texture2D(uTex, uv + uTexel * vec2(-0.5, -0.5)).rgb + texture2D(uTex, uv + uTexel * vec2(0.5, -0.5)).rgb;
    gl_FragColor = vec4(c * 0.25 * uDark * safeAtt(vUv), 1.0);
  }
`;

// Materials and geometries live at module level: on the Pi a shader is
// compiled in seconds and would be recompiled at every appearance (see
// nebula.js's VOLUME_MATERIALS). The per-flower uniform arrays are adopted
// by each instance and reset in init.
let SHARED = null;
function shared() {
  if (SHARED) return SHARED;
  const mats = [];
  for (let i = 0; i < MAX_FLOWERS; i++) mats.push(new THREE.Matrix4());
  const flowerU = {
    uFlower: { value: mats },
    uOpen: { value: new Float32Array(MAX_FLOWERS) },
    uLevel: { value: new Float32Array(MAX_FLOWERS) },
    uHue: { value: new Float32Array(MAX_FLOWERS) },
    uColor: { value: Array.from({ length: MAX_FLOWERS }, () => new THREE.Vector3(1, 1, 1)) }
  };
  // Premultiplied blending everywhere: opaque where alpha is 1, a soft
  // edge where the edge ramp lowers it, and the MSAA target resolves to
  // premultiplied colour that the display quad lays over the rosace.
  const premultiplied = {
    blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor,
    blendSrcAlpha: THREE.OneFactor, blendDstAlpha: THREE.OneMinusSrcAlphaFactor
  };
  const petal = new THREE.ShaderMaterial(Object.assign({
    uniforms: {
      uFlower: flowerU.uFlower, uOpen: flowerU.uOpen, uLevel: flowerU.uLevel, uHue: flowerU.uHue, uColor: flowerU.uColor,
      uCrowns: { value: 3 }, uIri: { value: null }, uChrome: { value: null }, uExposure: { value: EXPOSURE },
      uEdgeAA: { value: 0 }, uShowNormals: { value: 0 }
    },
    vertexShader: PETAL_VERTEX, fragmentShader: PETAL_FRAGMENT, side: THREE.DoubleSide,
    depthWrite: true, depthTest: true
  }, premultiplied));
  // The stamens ADD light and leave alpha alone: over the rosace they stay
  // additive once the private target is laid down.
  const stamen = new THREE.ShaderMaterial({
    uniforms: {
      uFlower: flowerU.uFlower, uOpen: flowerU.uOpen, uLevel: flowerU.uLevel, uColor: flowerU.uColor,
      uPx: { value: 40 }, uTime: { value: 0 }, uTwinkle: { value: 0.2 }, uBreath: { value: 1 }, uExposure: { value: EXPOSURE }
    },
    vertexShader: STAMEN_VERTEX, fragmentShader: STAMEN_FRAGMENT,
    transparent: true, depthWrite: false, depthTest: true,
    blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor, blendDst: THREE.OneFactor,
    blendSrcAlpha: THREE.ZeroFactor, blendDstAlpha: THREE.OneFactor
  });
  const display = new THREE.ShaderMaterial(Object.assign({
    uniforms: { uTex: { value: null } },
    vertexShader: QUAD_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false, transparent: true
  }, premultiplied));
  const matcapView = new THREE.ShaderMaterial({
    uniforms: { uA: { value: null }, uB: { value: null }, uAspect: { value: 1.78 } },
    vertexShader: QUAD_VERTEX, fragmentShader: MATCAP_VIEW_FRAGMENT, depthTest: false, depthWrite: false
  });
  const bg = new THREE.ShaderMaterial({
    uniforms: {
      uTex: { value: null }, uAspect: { value: 1.78 }, uZoom: { value: BG_ZOOM }, uDark: { value: BG_DARK }, uRot: { value: 0 }, uShift: { value: 0 },
      uTexel: { value: new THREE.Vector2(1 / 640, 1 / 360) },
      uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    },
    vertexShader: QUAD_VERTEX, fragmentShader: BG_FRAGMENT, depthTest: false, depthWrite: false
  });
  // the petal grid: position.x = s, position.y = v
  const pos = new Float32Array((SEG_S + 1) * (SEG_V + 1) * 3);
  let k = 0;
  for (let j = 0; j <= SEG_S; j++) for (let i = 0; i <= SEG_V; i++) { pos[k++] = j / SEG_S; pos[k++] = i / SEG_V * 2 - 1; pos[k++] = 0; }
  const idx = [];
  for (let j = 0; j < SEG_S; j++) for (let i = 0; i < SEG_V; i++) {
    const a = j * (SEG_V + 1) + i, b = a + SEG_V + 1;
    idx.push(a, b, b + 1, a, b + 1, a + 1);
  }
  SHARED = { flowerU, petal, stamen, bg, display, matcapView, gridPos: pos, gridIdx: idx };
  return SHARED;
}

function matcapTexture(m) {
  const t = new THREE.DataTexture(m.data, m.size, m.size, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.NoColorSpace;
  t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = false; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  t.needsUpdate = true;
  return t;
}

// The lounge colour: the cover's hue, saturation IMPOSED at 0.85 (the
// preset's), lightness 0.55 - in linear space.
const _hslC = { h: 0, s: 0, l: 0 };
function loungeColor(hex, out) {
  const o = out || new THREE.Color();
  o.set(hex).getHSL(_hslC);
  o.setHSL(_hslC.h, LOOK.saturation, 0.55);
  return o;
}

export class BloomVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = TIER[o.qualityTier] ? o.qualityTier : 2;
    this._layout = TIER[this._tier];
    const fo = urlParam('bloomopen');
    this._forcedOpen = fo != null && fo !== '' && Number.isFinite(parseFloat(fo)) ? clamp01(parseFloat(fo)) : null;
    const fn = parseInt(urlParam('bloomflowers'), 10);
    this._flowerCount = Number.isFinite(fn) ? Math.max(1, Math.min(MAX_FLOWERS, fn)) : this._layout.flowers;
    this._debugDrop = urlParam('bloomdrop') === '1';
    this._probe = urlParam('bloomprobe') === '1';
    // ?bloomnormals=1: the view-space normals as colours, nothing else
    this._showNormals = urlParam('bloomnormals') === '1';
    // ?bloommatcap=1: the two matcaps as discs, nothing else
    this._showMatcap = urlParam('bloommatcap') === '1';
    // ?bloomaa=msaa|edge|off (see the header)
    const aa = urlParam('bloomaa');
    // The default by tier, MEASURED on the Pi (1280x400, the whole frame with
    // the composer, 13/10/2026): MSAA cost +10 ms at tier 0 (17.8 -> 28.2),
    // +11 at tier 1 and +15 at tier 2, the edge ramp about +1 ms. So the
    // Pi's tiers (0, 1) take the edge ramp and tier 2 (desktop, TV) MSAA.
    this._aa = aa === 'edge' || aa === 'off' || aa === 'msaa' ? aa : (this._tier >= 2 ? 'msaa' : 'edge');
    // ?bloombg=0: no rosace (to measure what it costs)
    this._noBg = urlParam('bloombg') === '0';
    // no grain: it would lift the pure black; a low bloom - Totem's lesson,
    // wide light boxes at 0.6 left a grey veil around every object
    const debugView = this._showNormals || this._showMatcap;
    this._post = new VdjPost(debugView
      ? { bloomStrength: 0, grain: 0, vignette: 0, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 }
      : { bloomStrength: 0.35, bloomThreshold: 0.78, grain: 0, vignette: 0.40 });
    this._comfort = new VdjComfort(LOOK.comfort);
    this._seed = 7;
  }

  get postProcessing() { return this._post.value; }

  _random() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }

  init(scene, camera) {
    this._scene = scene; this._camera = camera;
    const S = shared();
    this._S = S;
    const L = this._layout;
    const n = this._flowerCount;
    this._crowns = L.crowns;

    // the camera, once: the scene manager's own drift works from here
    camera.fov = CAM_FOV;
    camera.position.set(0, 0, CAM_Z);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld(true);       // unproject below reads it (stale-matrix trap)

    // the petals: one InstancedMesh
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(S.gridPos, 3));
    geo.setIndex(S.gridIdx);
    const fl = [], cr = [], ix = [];
    for (let f = 0; f < n; f++) for (let c = 0; c < L.crowns; c++) for (let i = 0; i < CROWNS[c].n; i++) { fl.push(f); cr.push(c); ix.push(i); }
    geo.setAttribute('aFlower', new THREE.InstancedBufferAttribute(new Float32Array(fl), 1));
    geo.setAttribute('aCrown', new THREE.InstancedBufferAttribute(new Float32Array(cr), 1));
    geo.setAttribute('aIndex', new THREE.InstancedBufferAttribute(new Float32Array(ix), 1));
    geo.instanceCount = fl.length;
    this._petalGeo = geo;
    this._petals = new THREE.Mesh(geo, S.petal);
    this._petals.frustumCulled = false;
    S.petal.uniforms.uCrowns.value = L.crowns;

    // the stamens: one Points
    const sg = new THREE.BufferGeometry();
    const N = n * STAMENS;
    const sPos = new Float32Array(N * 3), sF = new Float32Array(N), sDir = new Float32Array(N * 3), sSeed = new Float32Array(N);
    for (let f = 0; f < n; f++) for (let k = 0; k < STAMENS; k++) {
      const q = f * STAMENS + k;
      // a Fibonacci sphere, the upper hemisphere favoured (the heart faces out)
      const y = 1 - 2 * (k + 0.5) / STAMENS, rr = Math.sqrt(1 - y * y), th = k * 2.399963;
      sDir[q * 3] = Math.cos(th) * rr; sDir[q * 3 + 1] = Math.sin(th) * rr; sDir[q * 3 + 2] = Math.abs(y) * 0.6 + 0.4 * y;
      sF[q] = f; sSeed[q] = this._random();
    }
    sg.setAttribute('position', new THREE.BufferAttribute(sPos, 3));
    sg.setAttribute('aFlower', new THREE.BufferAttribute(sF, 1));
    sg.setAttribute('aDir', new THREE.BufferAttribute(sDir, 3));
    sg.setAttribute('aSeed', new THREE.BufferAttribute(sSeed, 1));
    this._stamenGeo = sg;
    this._stamens = new THREE.Points(sg, S.stamen);
    this._stamens.frustumCulled = false;

    this._stamens.renderOrder = 1;
    this._group = new THREE.Group();
    this._group.add(this._petals);
    this._group.add(this._stamens);
    S.petal.uniforms.uEdgeAA.value = this._aa === 'edge' ? 1 : 0;
    S.petal.uniforms.uShowNormals.value = this._showNormals ? 1 : 0;
    if (this._showNormals) this._stamens.visible = false;
    // MSAA: the flowers live in a private scene, rendered into a 4x
    // multisampled target by the display quad, which lays it over the
    // rosace. Otherwise they are drawn in the shared scene directly.
    this._msaaTarget = null;
    this._display = null;
    if (this._aa === 'msaa' && !this._showMatcap) {
      this._private = new THREE.Scene();
      this._private.add(this._group);
      this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), S.display);
      this._display.frustumCulled = false; this._display.renderOrder = 10;
      const self = this;
      this._display.onBeforeRender = function (renderer, sc, cam) { self._renderFlowers(renderer, cam); };
      scene.add(this._display);
    } else {
      this._private = null;
      scene.add(this._group);
    }
    if (this._showMatcap) {
      this._group.visible = false;
      this._matcapQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), S.matcapView);
      this._matcapQuad.frustumCulled = false; this._matcapQuad.renderOrder = 100;
      scene.add(this._matcapQuad);
    }

    // the far rosace
    this._bg = null; this._bgTarget = null;
    if (L.bgScale > 0 && !this._showNormals && !this._showMatcap && !this._noBg) {
      this._bg = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), S.bg);
      this._bg.frustumCulled = false; this._bg.renderOrder = -1000;
      const self = this;
      this._bg.onBeforeRender = function (renderer, sc, cam) { self._renderBackground(renderer, cam); };
      scene.add(this._bg);
    }
    this._lumTarget = null; this._lumPixels = null; this._bgPixels = null;

    // the flowers' state
    this._flowers = [];
    this._cycleBars = OPEN_BARS * Math.max(n, 4);
    for (let f = 0; f < n; f++) {
      this._flowers.push({
        slot: -1, r: MIN_SLOT_R, offset: f * this._cycleBars / n, spinDir: f % 2 ? -1 : 1,
        O: 0, level: 1, push: 0, swapped: false, hue: 0, hueFrom: 0, hueTo: 0, phase: 'opening',
        mat: new THREE.Matrix4(), pos: new THREE.Vector3(), scale: 1, axis: new THREE.Vector3(0, 0, 1)
      });
    }
    for (let f = 0; f < MAX_FLOWERS; f++) { S.flowerU.uLevel.value[f] = 0; S.flowerU.uOpen.value[f] = 0; }

    this._textures = { iridescent: null, chrome: null };
    this._paletteSeen = undefined;
    this._hueShift = 0;             // where the palette assignment stands
    this._hueFadeAt = -1e9;
    this._colours = [];

    // the musical state
    this._time = 0; this._frame = 0; this._paused = false;
    this._beatCount = 0; this._beatCont = 0; this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrasePhase = null;
    this._sinceBeat = 10; this._sinceBar = 10; this._free = 0; this._beatS = 0.5;
    this._phrases = 0; this._lastMode = null; this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._dropAtBars = -1e9; this._low = 0.4; this._high = 0.3;
    this._gain = 1; this._lumMeasured = null;
    this._slotsKey = null; this._slotGeom = null;
    this._applyPalette({ artworkPalette: null });
    this._layoutSlots();
    if (this._debugDrop) this._dropAtBars = 0;
    this._place();
    if (this._probe && typeof window !== 'undefined') window.__bloom = this;
  }

  // --- the palette and the matcaps ----------------------------------------------------
  _applyPalette(profile) {
    const pal = profile && profile.artworkPalette;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : FALLBACK_COLORS;
    const key = v.map(String).join(',');
    if (key === this._paletteSeen && this._textures.iridescent) return;
    this._paletteSeen = key;
    this._colours = v.map((h) => loungeColor(h));
    const mc = [this._colours[0], this._colours[1 % this._colours.length], this._colours[2 % this._colours.length]];
    for (const k of ['iridescent', 'chrome']) {
      if (this._textures[k]) this._textures[k].dispose();
      this._textures[k] = matcapTexture(genererMatcap(k, mc, 256, MATCAP_OPTS));
    }
    this._S.petal.uniforms.uIri.value = this._textures.iridescent;
    this._S.petal.uniforms.uChrome.value = this._textures.chrome;
    this._S.matcapView.uniforms.uA.value = this._textures.iridescent;
    this._S.matcapView.uniforms.uB.value = this._textures.chrome;
    this._baseHue = this._hueOf(this._colours[0]);
    this._assignHues(true);
  }

  _hueOf(c) { const h = { h: 0, s: 0, l: 0 }; c.getHSL(h); return h.h; }

  // Each flower one hue of the palette; `instant` for a new palette, else a
  // one-bar fade (the lounge's allowed transition) along the shortest way.
  _assignHues(instant) {
    const cols = this._colours;
    for (let f = 0; f < this._flowers.length; f++) {
      const F = this._flowers[f];
      const c = cols[(f + this._hueShift) % cols.length];
      let to = this._hueOf(c) - this._baseHue;
      const from = F.hue;
      to = from + (((to - from) % 1) + 1.5) % 1 - 0.5;   // shortest way round
      F.hueFrom = instant ? to : from; F.hueTo = to;
      F.colour = c;
    }
    this._hueFadeAt = instant ? -1e9 : this._time;
  }

  // --- the composition ------------------------------------------------------------------
  // Every slot, solved for the current aspect and cover: its centre pulled
  // in until the WHOLE open flower (radius r, height units) sits inside the
  // frame with EDGE_MARGIN, off the cover, and out of the Broadcast button's
  // corner; r itself at most its wish and MAX_R. Then the world position at
  // the slot's depth, and the flower's scale so that its projected bounding
  // sphere - measured, perspective included (a sphere off the axis projects
  // larger than r / (d tan(fov/2)): up to 45% in this frame's corners) - is
  // exactly r. A flower's radius (F.r) is further limited by the other
  // flowers' discs; a slot that leaves less than MIN_SLOT_R is not taken.
  _solveSlot(s, aspect, sc, sh) {
    const sd = (x, y, cx, cy, hw, hh) => {
      const qx = Math.abs(x - cx) - hw, qy = Math.abs(y - cy) - hh;
      return Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0);
    };
    const bw = aspect * BROADCAST_W, bh = BROADCAST_H, bx = aspect - bw, by = 1 - bh;
    let hx = s.x * aspect, hy = s.y, r = Math.min(s.r, MAX_R);
    let cover = 0, broadcast = 0, edge = 0;
    for (let it = 0; it < 12; it++) {
      const m = r + EDGE_MARGIN + DRIFT_MARGIN;
      hx = m < aspect ? Math.max(-aspect + m, Math.min(aspect - m, hx)) : 0;
      hy = m < 1 ? Math.max(-1 + m, Math.min(1 - m, hy)) : 0;
      edge = Math.min(aspect - Math.abs(hx), 1 - Math.abs(hy)) - EDGE_MARGIN - DRIFT_MARGIN;
      cover = sd(hx, hy, sc.x, sc.y, sh.x, sh.y) - COVER_MARGIN - DRIFT_MARGIN;
      broadcast = sd(hx, hy, bx, by, bw, bh) - BROADCAST_MARGIN - DRIFT_MARGIN;
      const nr = Math.min(r, edge, cover, broadcast);
      if (Math.abs(nr - r) < 1e-5) break;
      r = nr;
    }
    return { hx, hy, r, edge, cover, broadcast };
  }

  _layoutSlots() {
    const aspect = safeZoneUniforms.uAspect.value || 16 / 9;
    // R19: the slots keep off the stage block, not off wherever the cover is
    const sc = safeZoneUniforms.uStageCenter.value, sh = safeZoneUniforms.uStageHalfSize.value;
    const key = [aspect, sc.x, sc.y, sh.x, sh.y].map((x) => Math.round(x * 500)).join(',');
    if (key === this._slotsKey) return;
    this._slotsKey = key;
    const cam = this._camera;
    cam.aspect = aspect; cam.updateProjectionMatrix(); cam.updateMatrixWorld(true);
    const geom = [];
    const camPos = cam.position.clone();
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(cam.quaternion);
    for (const s of SLOTS) {
      const q = this._solveSlot(s, aspect, sc, sh);
      const ndc = new THREE.Vector3(q.hx / aspect, q.hy, 0.5).unproject(cam);
      const dir = ndc.sub(camPos).normalize();
      const pos = camPos.clone().addScaledVector(dir, s.d / Math.max(0.2, dir.dot(fwd)));
      const out = new THREE.Vector3(q.hx, q.hy, 0).normalize();
      const axis = camPos.clone().sub(pos).normalize().addScaledVector(out, Math.tan(TILT)).normalize();
      // projected radius, in height units, of a sphere of radius 1 here
      const unitR = this._projectedRadius(pos, 1, q.hx, q.hy, aspect);
      geom.push({ hx: q.hx, hy: q.hy, room: Math.min(q.cover, q.edge, q.broadcast), rMax: q.r, pos, axis, d: s.d, unitR });
    }
    this._slotGeom = geom;
    // a new frame: deal the slots again, in order
    for (const F of this._flowers) { F.slot = -1; F.hidden = false; }
    for (const F of this._flowers) {
      for (const k of SLOT_ORDER) { const r = this._fitR(F, k); if (r >= MIN_SLOT_R) { F.slot = k; F.r = r; break; } }
      // no room left (a tall, narrow screen): the flower sits out rather
      // than being forced onto another one or the cover
      if (F.slot < 0) { F.hidden = true; F.slot = 0; F.r = 0; }
    }
  }

  // The largest distance, in height units, between (hx, hy) and the
  // projection of a sphere of radius R at `pos` (sampled on 26 directions).
  _projectedRadius(pos, R, hx, hy, aspect) {
    const cam = this._camera, v = new THREE.Vector3();
    let m = 0;
    for (let a = -1; a <= 1; a++) for (let b = -1; b <= 1; b++) for (let c = -1; c <= 1; c++) {
      if (!a && !b && !c) continue;
      v.set(a, b, c).normalize().multiplyScalar(R).add(pos).project(cam);
      m = Math.max(m, Math.hypot(v.x * aspect - hx, v.y - hy));
    }
    return m;
  }

  // The radius flower F could have on slot k, given the others' discs.
  // The two ANCHORS - the big flower bottom left, the far one top right -
  // keep their whole disc reserved even while their flower is away, or a
  // neighbour settles into it and the big flower comes back small.
  _fitR(F, k) {
    const g = this._slotGeom[k];
    let r = g.rMax;
    const taken = new Set();
    for (const G of this._flowers) {
      if (G === F || G.slot < 0 || G.hidden) continue;
      if (G.slot === k) return 0;
      taken.add(G.slot);
      const h = this._slotGeom[G.slot];
      r = Math.min(r, Math.hypot(g.hx - h.hx, g.hy - h.hy) - G.r - FLOWER_GAP);
    }
    for (const a of ANCHORS) {
      if (a === k || taken.has(a) || this._slotGeom[a].rMax < MIN_SLOT_R) continue;
      const h = this._slotGeom[a];
      r = Math.min(r, Math.hypot(g.hx - h.hx, g.hy - h.hy) - h.rMax - FLOWER_GAP);
    }
    return r;
  }

  // A new slot for a flower that has gone away: any free one with room,
  // the roomier ones more likely.
  _moveFlower(F) {
    const cands = [];
    for (let k = 0; k < SLOTS.length; k++) {
      if (k === F.slot) continue;
      const r = this._fitR(F, k);
      if (r >= MIN_SLOT_R) cands.push({ k, r });
    }
    if (!cands.length) {
      // nowhere else to go: stay put if the current place still fits
      const r = this._fitR(F, F.slot);
      if (r >= MIN_SLOT_R) { F.hidden = false; F.r = r; } else { F.hidden = true; F.r = 0; }
      return;
    }
    F.hidden = false;
    let tot = 0;
    for (const c of cands) tot += c.r;
    let x = this._random() * tot;
    for (const c of cands) { x -= c.r; if (x <= 0) { F.slot = c.k; F.r = c.r; return; } }
    const c = cands[cands.length - 1]; F.slot = c.k; F.r = c.r;
  }

  // --- the cycle ----------------------------------------------------------------------------
  // The flower's opening, level and recession at bar count `bars`.
  _cycleState(F, bars) {
    const C = this._cycleBars;
    const p = (((bars + F.offset) % C) + C) % C;
    const away = C - OPEN_BARS - HOLD_BARS - CLOSE_BARS;
    if (p < OPEN_BARS) return { phase: 'opening', O: smooth(p / OPEN_BARS), level: 1, push: 0 };
    if (p < OPEN_BARS + HOLD_BARS) return { phase: 'open', O: this._breath(bars), level: 1, push: 0 };
    if (p < OPEN_BARS + HOLD_BARS + CLOSE_BARS) return { phase: 'closing', O: smooth(1 - (p - OPEN_BARS - HOLD_BARS) / CLOSE_BARS), level: 1, push: 0 };
    // away: recede and fade out over the first half, come back over the second
    const u = (p - OPEN_BARS - HOLD_BARS - CLOSE_BARS) / away;
    const e = u < 0.5 ? smooth(u * 2) : smooth((1 - u) * 2);
    return { phase: u < 0.5 ? 'leaving' : 'returning', O: 0, level: 1 - e, push: e, half: u < 0.5 ? 0 : 1 };
  }

  // The open flower's breath: between 1 - BREATH_DEPTH x amp and 1, one
  // breath per bar, a cosine of the CONTINUOUS bar count - 1 on every bar
  // line, so opening, holding and closing join without a step.
  _breath(bars) {
    const amp = 0.4 + 0.6 * this._low;
    return 1 - BREATH_DEPTH * amp * (0.5 - 0.5 * Math.cos(bars * Math.PI * 2));
  }

  // The drop's weight: 0 -> 1 over 2 bars, held 8, back over 2.
  _dropWeight(bars) {
    const t = bars - this._dropAtBars;
    if (t < 0) return 0;
    if (t < DROP_OPEN_BARS) return smooth(t / DROP_OPEN_BARS);
    if (t < DROP_OPEN_BARS + DROP_HOLD_BARS) return 1;
    if (t < DROP_OPEN_BARS + DROP_HOLD_BARS + DROP_RETURN_BARS) return smooth(1 - (t - DROP_OPEN_BARS - DROP_HOLD_BARS) / DROP_RETURN_BARS);
    return 0;
  }
  // The lounge drop's intensity: +25% over one bar, held through the rich
  // window, back over one bar.
  _dropIntensity(bars) {
    const t = bars - this._dropAtBars;
    const R = LOOK.dropRise;
    if (t < 0 || !R) return 1;
    let w;
    if (t < R.bars) w = smooth(t / R.bars);
    else if (t < LOOK.dropRichBars) w = 1;
    else if (t < LOOK.dropRichBars + 1) w = smooth(1 - (t - LOOK.dropRichBars));
    else w = 0;
    return 1 + R.gain * w;
  }

  // --- drops from the analysis marks (as Totem) ------------------------------------------
  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < 1500) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { this._applyPalette(profile); this._dropIdx = 0; this._lastNowMs = null; }

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._applyPalette(profile);
    this._layoutSlots();
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) {
      // the flowers keep their state, the rotation stops: nothing advances
      this._post.update(1);
      this._place();
      return;
    }
    this._time += dt; this._frame++;
    const director = profile.director;
    const bands = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    if (bands) { this._low += (clamp01(bands.low) - this._low) * Math.min(1, dt / 0.8); this._high += (clamp01(bands.high) - this._high) * Math.min(1, dt / 0.5); }
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._beatS = bpm > 0 ? 60 / bpm : 0.75;

    // the beat, the bar, the phrase
    let beat = false, bar = false, phrase = false;
    const bp = profile.beatPhase;
    if (bp != null) { if (this._lastBeatPhase != null && bp < this._lastBeatPhase - 0.5) beat = true; this._lastBeatPhase = bp; }
    else { this._free += dt; if (this._free >= this._beatS) { this._free -= this._beatS; beat = true; } }
    const barP = profile.barPhase;
    if (barP != null) { if (this._lastBarPhase != null && barP < this._lastBarPhase - 0.5) bar = true; this._lastBarPhase = barP; }
    else if (beat && (this._beatCount + 1) % 4 === 0) bar = true;
    const phP = profile.phrasePhase;
    if (phP != null) { if (this._lastPhrasePhase != null && phP < this._lastPhrasePhase - 0.5) phrase = true; this._lastPhrasePhase = phP; }
    else if (beat && (this._beatCount + 1) % 16 === 0) phrase = true;
    if (beat) { this._beatCount++; this._sinceBeat = 0; } else this._sinceBeat += dt;
    if (bar) this._sinceBar = 0; else this._sinceBar += dt;
    // the continuous beat count: every loop in this file is a function of it
    const cont = bp != null ? this._beatCount + bp : this._beatCount + this._free / this._beatS;
    // A seek moves the cycle with the music; it is rare and a flower then
    // simply resumes where the song is.
    this._beatCont = cont;
    const bars = this._beatCont / 4;

    // PHRASE: one in two, a new distribution of the hues, faded over a bar
    if (phrase) {
      this._phrases++;
      if (this._phrases % 2 === 0) { this._hueShift++; this._assignHues(false); }
    }
    // DROP: no flash; every flower opens in two bars and stays open eight
    let drop = this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop && bars - this._dropAtBars > DROP_OPEN_BARS + DROP_HOLD_BARS) this._dropAtBars = bars;

    this._place();

    // the comfort: measured every LUM_EVERY frames (see _measure), a gain
    if (this._lumMeasured != null) { this._gain = this._comfort.measure(this._lumMeasured, dt * LUM_EVERY); this._lumMeasured = null; }
    // the lounge RGB split: the bar only, x1.5, 300 ms
    this._post.update(vdjBarChroma(this._sinceBar));
  }

  // Everything the frame needs, from the musical state.
  _place() {
    const S = this._S, U = S.flowerU;
    const bars = this._beatCont / 4;
    const dropW = this._forcedOpen != null ? 0 : this._dropWeight(bars);
    const fadeU = smooth((this._time - this._hueFadeAt) / (4 * this._beatS));
    const spin = vdjLoop(this._beatCont, SPIN_BEATS) * Math.PI * 2;
    const tmpQ = new THREE.Quaternion(), tmpS = new THREE.Quaternion(), zAxis = new THREE.Vector3(0, 0, 1);
    const camPos = this._camera.position;
    const tanH = Math.tan(CAM_FOV * Math.PI / 360);
    for (let f = 0; f < this._flowers.length; f++) {
      const F = this._flowers[f];
      let st;
      if (this._forcedOpen != null) st = { phase: 'forced', O: this._forcedOpen, level: 1, push: 0 };
      else st = this._cycleState(F, bars);
      // the move: once, while invisible, half-way through being away
      if (st.phase === 'returning' && !F.swapped) { this._moveFlower(F); F.swapped = true; }
      if (st.phase !== 'returning' && st.phase !== 'leaving') F.swapped = false;
      F.phase = st.phase;
      const O = st.O + (this._breath(bars) - st.O) * dropW;
      const level = st.level + (1 - st.level) * dropW;
      const push = st.push * (1 - dropW);
      F.O = O; F.level = F.hidden ? 0 : level; F.push = push;
      F.hue = F.hueFrom + (F.hueTo - F.hueFrom) * fadeU;
      const g = this._slotGeom[F.slot];
      // receding: further along the line of sight, same size, so smaller
      F.pos.copy(g.pos).sub(camPos).multiplyScalar(1 + RECEDE * push).add(camPos);
      tmpQ.setFromUnitVectors(zAxis, g.axis);
      tmpS.setFromAxisAngle(zAxis, spin * F.spinDir + f * 1.3);
      tmpQ.multiply(tmpS);
      // the scale whose projected bounding sphere is F.r (see _layoutSlots)
      const sc = F.r / (g.unitR * R_BOUND);
      F.scale = sc;
      F.mat.compose(F.pos, tmpQ, new THREE.Vector3(sc, sc, sc));
    }
    // THE DRAW ORDER: the instances are laid out flower by flower (uniform
    // slot 0 first) and, inside a flower, crown by crown from the outside in.
    // The flowers are assigned to the uniform slots from the farthest to the
    // nearest, so the edge ramp's soft pixels always blend over what is
    // behind them.
    const order = this._flowers.map((F, f) => f).sort((a, b) => this._flowers[b].pos.distanceToSquared(camPos) - this._flowers[a].pos.distanceToSquared(camPos));
    this._uslot = new Array(this._flowers.length);
    for (let k = 0; k < order.length; k++) {
      const F = this._flowers[order[k]];
      this._uslot[order[k]] = k;
      U.uOpen.value[k] = F.O;
      U.uLevel.value[k] = F.level;
      U.uHue.value[k] = F.hue;
      U.uColor.value[k].set(F.colour.r, F.colour.g, F.colour.b);
    }
    // the bouquet's drift (the lounge camera): zoom +-2%, roll +-1.2 deg,
    // a small sway, all 32- and 64-beat sines of the continuous beat count
    const a64 = vdjLoop(this._beatCont, 64) * Math.PI * 2, a32 = vdjLoop(this._beatCont, 32) * Math.PI * 2;
    this._group.scale.setScalar(1 + 0.02 * Math.sin(a64));
    this._group.rotation.z = (1.2 * Math.PI / 180) * Math.sin(a32 + 0.7);
    this._group.position.set(0.12 * Math.sin(a64 + 1.9), 0.08 * Math.sin(a32 * 0.5 + 0.4), 0);
    this._group.updateMatrixWorld(true);
    for (let f = 0; f < this._flowers.length; f++) U.uFlower.value[this._uslot[f]].multiplyMatrices(this._group.matrixWorld, this._flowers[f].mat);
    for (let f = this._flowers.length; f < MAX_FLOWERS; f++) U.uLevel.value[f] = 0;
    // the light
    const intensity = this._forcedOpen != null ? 1 : this._dropIntensity(bars);
    S.petal.uniforms.uExposure.value = EXPOSURE * this._gain * intensity;
    S.stamen.uniforms.uExposure.value = EXPOSURE * this._gain * intensity;
    S.stamen.uniforms.uBreath.value = this._paused ? 1 : vdjBeatBreath(this._sinceBeat, LOOK);
    S.stamen.uniforms.uTwinkle.value = 0.08 + 0.32 * this._high;
    S.stamen.uniforms.uTime.value = this._time % 62.83;
    // gl_PointSize = uPx x flower scale / view depth: STAMEN_PX pixels for a
    // unit-scale flower 10 units away, on a 720-pixel-high buffer
    S.stamen.uniforms.uPx.value = STAMEN_PX * 10 * (getRenderBufferHeight() || 720) / 720;
    if (this._bg) {
      const aspect = safeZoneUniforms.uAspect.value || 1.78;
      const B = S.bg.uniforms;
      B.uAspect.value = aspect;
      B.uDark.value = BG_DARK * this._gain * intensity;
      B.uShift.value = safeZoneUniforms.uStageCenter.value.y / 2;   // R19: turns about the stage centre
      B.uRot.value = vdjLoop(this._beatCont, BG_TURN_BEATS) * Math.PI * 2;
    }
    if (this._matcapQuad) S.matcapView.uniforms.uAspect.value = safeZoneUniforms.uAspect.value || 1.78;
  }

  // The flowers into the private 4x MSAA target (the composer's size), from
  // the shared camera; the display quad then lays it over the rosace.
  _renderFlowers(renderer, camera) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; }
    else { const b = renderer.getDrawingBufferSize(new THREE.Vector2()); w = b.x; h = b.y; }
    if (!this._msaaTarget || this._msaaTarget.width !== w || this._msaaTarget.height !== h) {
      if (this._msaaTarget) this._msaaTarget.dispose();
      this._msaaTarget = new THREE.WebGLRenderTarget(w, h, {
        samples: 4, depthBuffer: true,
        // the stamens' cores go above 1: half floats where the tier allows
        type: this._tier >= 2 ? THREE.HalfFloatType : THREE.UnsignedByteType,
        minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter
      });
    }
    const clearPrev = renderer.getClearColor(new THREE.Color()), alphaPrev = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    renderer.setRenderTarget(this._msaaTarget);
    renderer.setClearColor(0x000000, 0);
    renderer.clear(true, true, false);
    renderer.render(this._private, camera);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(clearPrev, alphaPrev);
    renderer.xr.enabled = xr;
    this._S.display.uniforms.uTex.value = this._msaaTarget.texture;
  }

  // The far rosace: the flowers again, gathered round the target's centre
  // (where the kaleidoscope reads), big, in the frontal plane - the petals
  // then always straddle a fold line and weld into one rosace. Every
  // LUM_EVERY frames the comfort measurement rides along.
  _renderBackground(renderer, camera) {
    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    const w = Math.max(8, Math.round(size.x * this._layout.bgScale)), h = Math.max(8, Math.round(size.y * this._layout.bgScale));
    if (!this._bgTarget || this._bgTarget.width !== w || this._bgTarget.height !== h) {
      if (this._bgTarget) this._bgTarget.dispose();
      this._bgTarget = new THREE.WebGLRenderTarget(w, h, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: true, type: THREE.UnsignedByteType, wrapS: THREE.MirroredRepeatWrapping, wrapT: THREE.MirroredRepeatWrapping, samples: 4 });
      this._S.bg.uniforms.uTexel.value.set(1 / w, 1 / h);
    }
    const U = this._S.flowerU;
    const saved = U.uFlower.value.map((m) => m.clone());
    const n = this._flowers.length;
    const ring = vdjLoop(this._beatCont, 32) * Math.PI * 2;
    const tanH = Math.tan(CAM_FOV * Math.PI / 360);
    const q = new THREE.Quaternion(), zAxis = new THREE.Vector3(0, 0, 1), spinQ = new THREE.Quaternion();
    for (let f = 0; f < n; f++) {
      const a = ring + f * Math.PI * 2 / n;
      const p = new THREE.Vector3(BG_R * Math.cos(a), BG_R * Math.sin(a), 0);
      spinQ.setFromAxisAngle(zAxis, vdjLoop(this._beatCont, SPIN_BEATS) * Math.PI * 2 * (f % 2 ? -1 : 1));
      q.copy(spinQ);
      const sc = BG_FLOWER_R / R_BOUND;
      U.uFlower.value[f].compose(p, q, new THREE.Vector3(sc, sc, sc));
    }
    const prev = renderer.getRenderTarget();
    const clearPrev = renderer.getClearColor(new THREE.Color()), alphaPrev = renderer.getClearAlpha();
    this._bg.visible = false;
    renderer.setRenderTarget(this._bgTarget);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    this._stamens.visible = false;
    renderer.render(this._petals, camera);
    this._stamens.visible = true;
    for (let f = 0; f < n; f++) U.uFlower.value[f].copy(saved[f]);
    // the comfort measurement: the real flowers, small, every LUM_EVERY frames
    if (this._frame % LUM_EVERY === 0) this._measure(renderer, camera);
    renderer.setRenderTarget(prev);
    renderer.setClearColor(clearPrev, alphaPrev);
    this._bg.visible = true;
    this._S.bg.uniforms.uTex.value = this._bgTarget.texture;
  }

  // The frame's mean luminance, display-encoded (sqrt of the linear
  // luminance, about what an 8-bit frame averages): the flowers rendered
  // into a 160 x 90 target, plus the rosace's share.
  _measure(renderer, camera) {
    if (!this._lumTarget) {
      this._lumTarget = new THREE.WebGLRenderTarget(160, 90, { depthBuffer: true, type: THREE.UnsignedByteType });
      this._lumPixels = new Uint8Array(160 * 90 * 4);
    }
    const prev = renderer.getRenderTarget();
    const clearPrev = renderer.getClearColor(new THREE.Color()), alphaPrev = renderer.getClearAlpha();
    renderer.setRenderTarget(this._lumTarget);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    const bgVis = this._bg ? this._bg.visible : false;
    if (this._bg) this._bg.visible = false;
    renderer.render(this._group, camera);
    renderer.readRenderTargetPixels(this._lumTarget, 0, 0, 160, 90, this._lumPixels);
    let s = 0;
    const px = this._lumPixels;
    for (let i = 0; i < px.length; i += 4) s += Math.sqrt((0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2]) / 255);
    let fg = s / (px.length / 4);
    if (this._bgTarget) {
      // the rosace: its mean linear luminance x the darkening, encoded
      const w = this._bgTarget.width, h = this._bgTarget.height;
      if (!this._bgPixels || this._bgPixels.length !== w * h * 4) this._bgPixels = new Uint8Array(w * h * 4);
      renderer.readRenderTargetPixels(this._bgTarget, 0, 0, w, h, this._bgPixels);
      let b = 0, c = 0;
      for (let i = 0; i < this._bgPixels.length; i += 4 * 9) { b += (0.2126 * this._bgPixels[i] + 0.7152 * this._bgPixels[i + 1] + 0.0722 * this._bgPixels[i + 2]) / 255; c++; }
      fg += Math.sqrt(Math.max(0, b / c * BG_DARK));
    }
    if (this._bg) this._bg.visible = bgVis;
    renderer.setRenderTarget(prev);
    renderer.setClearColor(clearPrev, alphaPrev);
    this._lumMeasured = fg;
  }

  getProbe() {
    const bars = this._beatCont / 4;
    return {
      tier: this._tier, flowers: this._flowers.length, crowns: this._crowns, beats: this._beatCont, bars,
      cycleBars: this._cycleBars, dropWeight: this._dropWeight(bars), intensity: this._dropIntensity(bars),
      state: this._flowers.map((F) => ({ hidden: !!F.hidden, phase: F.phase, O: +F.O.toFixed(3), level: +F.level.toFixed(3), slot: F.slot, hue: +F.hue.toFixed(3), pos: F.pos.toArray().map((x) => +x.toFixed(2)) })),
      slots: this._slotGeom ? this._slotGeom.map((g) => ({ hx: +g.hx.toFixed(3), hy: +g.hy.toFixed(3), room: +g.room.toFixed(3), rMax: +g.rMax.toFixed(3) })) : null,
      radii: this._flowers.map((F) => +F.r.toFixed(3)), aa: this._aa,
      gain: this._gain, lumAvg: this._comfort.average, aberration: this._post.value.aberration
    };
  }

  dispose() {
    if (this._scene) {
      this._scene.remove(this._group);
      if (this._bg) { this._bg.onBeforeRender = null; this._scene.remove(this._bg); this._bg.geometry.dispose(); }
      if (this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); this._display.geometry.dispose(); }
      if (this._matcapQuad) { this._scene.remove(this._matcapQuad); this._matcapQuad.geometry.dispose(); }
    }
    if (this._private) { this._private.remove(this._group); this._private = null; }
    if (this._msaaTarget) { this._msaaTarget.dispose(); this._msaaTarget = null; }
    if (this._S) this._S.display.uniforms.uTex.value = null;
    if (this._petalGeo) this._petalGeo.dispose();
    if (this._stamenGeo) this._stamenGeo.dispose();
    if (this._bgTarget) { this._bgTarget.dispose(); this._bgTarget = null; }
    if (this._lumTarget) { this._lumTarget.dispose(); this._lumTarget = null; }
    for (const k of ['iridescent', 'chrome']) if (this._textures && this._textures[k]) { this._textures[k].dispose(); this._textures[k] = null; }
    if (this._S) { this._S.petal.uniforms.uIri.value = null; this._S.petal.uniforms.uChrome.value = null; this._S.bg.uniforms.uTex.value = null; }
    if (typeof window !== 'undefined' && window.__bloom === this) window.__bloom = null;
    this._scene = null;
  }
}

